import type { UseEmblaCarouselType } from 'embla-carousel-react';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import useEmblaCarousel from 'embla-carousel-react';
import { createContext, use, useState, useCallback, useEffect } from 'react';

import { Button } from '@/components/ui/button/Button';

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];
export type CarouselApi = UseEmblaCarouselType[1];

interface CarouselProps {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
}

export type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

export function useCarousel() {
  const context = use(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

export function Carousel(carouselProps: ComponentProps<'div'> & CarouselProps) {
  const { orientation = 'horizontal', opts, setApi, plugins, className, children, ...props } = carouselProps;

  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const [canScrollNext, setCanScrollNext] = useState<boolean>(false);

  const onSelect = useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: opts?.axis === 'y' ? 'vertical' : orientation,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div onKeyDownCapture={handleKeyDown} className={cn('relative', className)} role="region" aria-roledescription="carousel" data-slot="carousel" {...props}>
        {children}
      </div>
    </CarouselContext>
  );
}

export function CarouselContent(carouselContentProps: ComponentProps<'div'>) {
  const { className, ...props } = carouselContentProps;
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden" data-slot="carousel-content">
      <div className={cn('flex', orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col', className)} {...props} />
    </div>
  );
}

export function CarouselItem(carouselItemProps: ComponentProps<'div'>) {
  const { className, ...props } = carouselItemProps;
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn('min-w-0 shrink-0 grow-0 basis-full', orientation === 'horizontal' ? 'pl-4' : 'pt-4', className)}
      {...props}
    />
  );
}

export function CarouselPrevious(carouselPreviousProps: ComponentProps<typeof Button>) {
  const { className, variant = 'outline', size = 'sm', children, ...props } = carouselPreviousProps;

  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      size={size}
      variant={variant}
      className={cn('absolute size-8', orientation === 'horizontal' ? 'top-1/2 left-2 -translate-y-1/2' : 'top-2 left-1/2 -translate-x-1/2 rotate-90', className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      {children}
      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

export function CarouselNext(carouselNextProps: ComponentProps<typeof Button>) {
  const { className, variant = 'outline', size = 'sm', children, ...props } = carouselNextProps;
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      data-slot="carousel-next"
      size={size}
      variant={variant}
      className={cn('absolute size-8', orientation === 'horizontal' ? 'top-1/2 right-2 -translate-y-1/2' : 'bottom-2 left-1/2 -translate-x-1/2 rotate-90', className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      {children}
      <span className="sr-only">Next slide</span>
    </Button>
  );
}
